import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { exhibitionService } from '../services/api';
import ExhibitionGrid from '../components/ExhibitionGrid';
import SearchBar from '../components/SearchBar';
import FilterPanel from '../components/FilterPanel';
import { SkeletonGrid } from '../components/SkeletonCard';
import ErrorMessage from '../components/ErrorMessage';
import { getUserFacingApiError } from '../utils/apiErrors';
import EmptyState from '../components/EmptyState';
import { useSEO } from '../utils/useSEO';

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [exhibitions, setExhibitions] = useState([]);
  const [displayExhibitions, setDisplayExhibitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'date-asc');
  
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || '',
    city: searchParams.get('city') || '',
    artist: searchParams.get('artist') || '',
  });
  
  const [quickFilters, setQuickFilters] = useState({
    openingSoon: false,
    endingSoon: false,
  });
  
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');

  useSEO({
    title: 'Explore Events',
    description: 'Search and filter current and upcoming art exhibitions and cultural events.',
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);

    const params = {};
    if (searchQuery) params.search = searchQuery;
    if (filters.status) params.status = filters.status;
    if (filters.city) params.city = filters.city;
    if (filters.artist) params.artist = filters.artist;
    if (sortBy) params.sort = sortBy;

    // Update URL params
    setSearchParams(params);

    exhibitionService.getFiltered(params)
      .then(setExhibitions)
      .catch((err) => {
        console.error('Error fetching exhibitions:', err);
        setError(getUserFacingApiError(err));
      })
      .finally(() => setLoading(false));
  }, [searchQuery, filters, sortBy, setSearchParams]);

  // Apply quick filters and sorting
  useEffect(() => {
    let filtered = [...exhibitions];

    // Apply quick filters
    if (quickFilters.openingSoon) {
      filtered = filtered.filter(ex => {
        if (ex.status !== 'upcoming' || !ex.start_date) return false;
        const daysUntil = Math.ceil((new Date(ex.start_date) - new Date()) / (1000 * 60 * 60 * 24));
        return daysUntil >= 0 && daysUntil <= 14;
      });
    }

    if (quickFilters.endingSoon) {
      filtered = filtered.filter(ex => {
        if (ex.status !== 'current' || !ex.end_date) return false;
        const daysUntil = Math.ceil((new Date(ex.end_date) - new Date()) / (1000 * 60 * 60 * 24));
        return daysUntil >= 0 && daysUntil <= 14;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date-asc':
          return new Date(a.start_date) - new Date(b.start_date);
        case 'date-desc':
          return new Date(b.start_date) - new Date(a.start_date);
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        default:
          return 0;
      }
    });

    setDisplayExhibitions(filtered);
  }, [exhibitions, quickFilters, sortBy]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const toggleQuickFilter = (filterName) => {
    setQuickFilters(prev => ({
      ...prev,
      [filterName]: !prev[filterName]
    }));
  };

  const clearAllFilters = () => {
    setFilters({ status: '', city: '', artist: '' });
    setQuickFilters({ openingSoon: false, endingSoon: false });
    setSearchQuery('');
    setSortBy('date-asc');
  };

  const hasActiveFilters = 
    filters.status || filters.city || filters.artist || 
    quickFilters.openingSoon || quickFilters.endingSoon || 
    searchQuery;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-4">
            Explore Events
          </h1>
          <SearchBar 
            onSearch={setSearchQuery}
            placeholder="Search by event name, artist, or venue..."
          />

          {/* Quick Filter Chips */}
          <div className="mt-4 flex flex-wrap gap-3 items-center">
            <span className="text-sm text-slate-600 font-medium">Quick Filters:</span>
            <button
              onClick={() => toggleQuickFilter('openingSoon')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                quickFilters.openingSoon
                  ? 'bg-brand text-white'
                  : 'bg-white text-slate-700 border border-slate-300 hover:border-brand'
              }`}
            >
              ✨ Opening Soon (14 days)
            </button>
            <button
              onClick={() => toggleQuickFilter('endingSoon')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                quickFilters.endingSoon
                  ? 'bg-amber-500 text-white'
                  : 'bg-white text-slate-700 border border-slate-300 hover:border-amber-500'
              }`}
            >
              ⚠️ Ending Soon (14 days)
            </button>
            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                className="px-4 py-2 rounded-full text-sm font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
              >
                ✕ Clear All
              </button>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex flex-col md:flex-row gap-8">
          {/* Filter Panel */}
          <aside className="md:w-64 flex-shrink-0">
            <FilterPanel 
              filters={filters}
              onFilterChange={handleFilterChange}
              isMobile={isMobile}
            />
          </aside>

          {/* Results */}
          <main className="flex-1">
            {loading && <SkeletonGrid count={6} />}
            
            {error && <ErrorMessage message={error} />}

            {!loading && !error && (
              <>
                {/* Results count and sort */}
                <div className="mb-6 flex justify-between items-center">
                  <p className="text-slate-600">
                    Found <span className="font-semibold">{displayExhibitions.length}</span> event{displayExhibitions.length !== 1 ? 's' : ''}
                  </p>
                  
                  {/* Sort dropdown */}
                  <div className="flex items-center gap-2">
                    <label htmlFor="sort" className="text-sm text-slate-600 font-medium">
                      Sort by:
                    </label>
                    <select
                      id="sort"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                    >
                      <option value="date-asc">Date (Soonest First)</option>
                      <option value="date-desc">Date (Latest First)</option>
                      <option value="name-asc">Name (A-Z)</option>
                      <option value="name-desc">Name (Z-A)</option>
                    </select>
                  </div>
                </div>

                {/* Results grid */}
                {displayExhibitions.length > 0 ? (
                  <ExhibitionGrid exhibitions={displayExhibitions} />
                ) : (
                  <EmptyState
                    message="No events found"
                    subMessage="Try adjusting your search or filters"
                  />
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default SearchPage;
